import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginRequest, clearNotification, logout } from "../../store/actions/auth";
import { Button } from "@vds/buttons";
import { Loader } from "@vds/loaders";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import NewHeader from "../../layouts/NewHeader";
import ToastNotification from "../../global/components/ToastNotification";
import { afterLoginPageHavePermission } from "../../global/utility/checkPermission";
import { validatePassword as checkPasswordPolicy } from "../../global/constants/passwordPolicy";

const SignIn = (props) => {
  const location = useLocation();
  const [form, setForm] = useState({
    fields: {
      email: {
        value: "",
        error: null,
        isDirty: false,
      },
      password: {
        value: "",
        error: null,
        isDirty: false,
      },
    },
    isSubmitted: false,
    isPasswordFieldVisible: false,
    showPassword: false,
    rememberMe: false,
  });
  const [left, setLeft] = useState(false);
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const loading = useSelector((state) => state.auth.loading);
  const isLogin = useSelector((state) => state.auth.isLogin);
  const error = useSelector((state) => state.auth.error);
  const user = useSelector((state) => state.auth.user);
  const notification = useSelector((state) => state.auth.ui.notification);
  const [loginFailed, setLoginFailed] = useState(false);

  useEffect(() => {
    const email = localStorage.getItem("rememberedEmail");
    if (email) {
      validateEmail(email);
      setForm((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          email: {
            ...prev.fields.email,
            value: email.trim(),
          },
        },
        rememberMe: true,
      }));
    }
  }, []);

  const checkRememberMe = useCallback(() => {
    if (form.rememberMe) {
      localStorage.setItem("rememberedEmail", form.fields.email.value);
    }
  }, [form.fields.email.value, form.rememberMe]);

  useEffect(() => {
    if (isLogin) {
      const afterLoginUrl = afterLoginPageHavePermission(
        user?.role?.permissions
      );
      checkRememberMe();
      if (location?.state?.from) {
        navigate(location?.state?.from, { replace: true });
      } else if (afterLoginUrl) {
        navigate(afterLoginUrl, { replace: true });
      } else {
        sessionStorage.clear();
        localStorage.removeItem("lasActivity");
        dispatch(logout());
      }
    }
  }, [isLogin, checkRememberMe, navigate, user?.role?.permissions]);

  useEffect(() => {
    dispatch(clearNotification());
    if (error) {
      setForm((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          password: {
            ...prev.fields.password,
            value: "",
            error: null,
            isDirty: false,
          },
        },
      }));
    }
  }, [error]);

  const togglePasswordVisibility = () => {
    setForm((prev) => ({
      ...prev,
      showPassword: !prev.showPassword,
    }));
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email === "") {
      setForm((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          email: {
            value: email,
            error: "Enter email address",
            isDirty: true,
          },
        },
      }));
    } else if (!regex.test(email)) {
      setForm((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          email: {
            value: email,
            error: "Invalid email address",
            isDirty: true,
          },
        },
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          email: { value: email, error: null, isDirty: true },
        },
      }));
    }
  };

  const handleEmailChange = (e) => {
    const emailValue = e?.target?.value;
    validateEmail(emailValue);
  };

  const validatePassword = (password) => {
    if (password === "") {
      setForm((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          password: {
            value: password,
            error: "Enter password",
            isDirty: true,
          },
        },
      }));
    } else if (!checkPasswordPolicy(password)) {
      setForm((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          password: {
            value: password,
            error: "Invalid password or email",
            isDirty: true,
          },
        },
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          password: { value: password, error: null, isDirty: true },
        },
      }));
    }
  };

  const handlePasswordChange = (e) => {
    const passwordValue = e.target.value;
    validatePassword(passwordValue);
  };

  const handleRememberMeChange = (e) => {
    setForm((prev) => ({ ...prev, rememberMe: e.target.checked }));
  };

  const onLoginClicked = async () => {
    validateEmail(form.fields.email.value);
    localStorage.setItem("user_email", form.fields.email.value);
    if (form.fields.email.isDirty && form.fields.email.error === null) {
      setForm((prev) => ({ ...prev, isPasswordFieldVisible: true }));
    }
  };
  useEffect(() => {
    if (
      form.isSubmitted &&
      form.fields.email.error === null &&
      form.fields.password.error === null &&
      notification.isActive
    ) {
      setLoginFailed(true);
    }
  }, [
    form.isSubmitted,
    form.fields.email.error,
    form.fields.password.error,
    notification.isActive,
  ]);
  const onSubmitVal = async () => {
    dispatch(clearNotification());
    validateEmail(form.fields.email.value);
    if (form.rememberMe === true) {
      localStorage.setItem("user_email", form.fields.email.value);
    } else {
      localStorage.removeItem("rememberedEmail");
      localStorage.removeItem("user_email");
    }
    setForm((prev) => ({ ...prev, isSubmitted: true }));
    validatePassword(form.fields.password.value);
    if (
      form.fields.email.isDirty &&
      form.fields.email.error === null &&
      form.fields.password.isDirty &&
      form.fields.password.error === null
    ) {
      const selectedAccount = {
        userName: form.fields.email.value.trim(),
        password: form.fields.password.value.trim(),
      };
      dispatch(loginRequest(selectedAccount));
    }
  };

  return (
    <>
      <NewHeader left={left} setLeft={setLeft} navmenu={"hide"} />
      <div style={{ height: "calc(94vh - 50px)" }}>
        <Loader active={loading} />
        <br />
        <br />
        {/* {form.isSubmitted &&
          form.fields.email.isDirty &&
          form.fields.email.error === null &&
          form.fields.password.isDirty &&
          form.fields.password.error === null &&
          notification.isActive && (
            <ToastNotification
              type={notification.type}
              message={notification.message}
              description={notification.description}
              duration={1}
              style={notification.style}
            />
          )} */}
        <Grid2
          container
          marginTop={1}
          marginBottom={2}
          spacing={1}
          columns={{ xs: 12 }}
        >
          <Grid2 item xs={2} sm={2} md={3} lg={4} direction="row"></Grid2>
          <Grid2 item xs={12} sm={8} md={6} lg={4} direction="row">
            <div className="login-heading-container">
              <div className="login_heading">
                <h1 className="login_heading_h1">Login</h1>
                {/* {form.isSubmitted &&
                  form.fields.password.isDirty &&
                  form.fields.password.error && 
                  (
                  )} */}
                {form.isSubmitted &&
                  (form.fields.email.error ||
                    form.fields.password.error ||
                    loginFailed) && (
                    <div className="mb-3 login_failure_new">
                      <div className="login-header">
                        <span className="login-failed-text">
                          <i className="bi bi-exclamation-diamond input-icon-failure"></i>
                        </span>
                        <div>
                          <span className="login-failed-text">
                            Login failed
                          </span>
                          <p className="message-text">
                            Please verify your email and password.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                <div className="mb-3">
                  <label className="input-label-user" htmlFor="email">
                    Email
                  </label>
                  <div className="input-container">
                    <input
                      id="email"
                      type="text"
                      label="Email address"
                      className={`form-control login-form-control`}
                      style={{ boxShadow: "none" }}
                      value={form.fields.email.value}
                      onChange={handleEmailChange}
                      onKeyUp={(e) =>
                        e.key === "Enter" ? onLoginClicked() : null
                      }
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="input-label-user" htmlFor="password">
                    Password
                  </label>
                  <div className="input-container">
                    <input
                      id="password"
                      label="Password"
                      type={form.showPassword ? "text" : "password"}
                      className={`form-control login-form-control`}
                      style={{ boxShadow: "none" }}
                      value={form.fields.password.value}
                      onChange={handlePasswordChange}
                      onKeyUp={(e) =>
                        e.key === "Enter" ? onSubmitVal() : null
                      }
                    />
                    <span
                      className={"login-show-password"}
                      onClick={togglePasswordVisibility}
                      style={{
                        textDecoration: "underline",
                      }}
                    >
                      {form.showPassword ? "Hide" : "Show"}
                    </span>
                  </div>
                </div>

                <Grid2
                  container
                  spacing={1}
                  justifyContent="space-between"
                  style={{ marginTop: "16px" }}
                >
                  <Grid2 item xs={6}>
                    <div>
                      <label>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="rememberMe"
                          checked={form.rememberMe}
                          onChange={handleRememberMeChange}
                        />
                        Remember me
                      </label>
                    </div>
                  </Grid2>
                  <Grid2 item xs={6} display="flex" justifyContent="flex-end">
                    <Link
                      style={{ color: "#6F7171", textDecoration: "none" }}
                      className="input-label-user"
                      to="/forgotpassword"
                    >
                      Forgot password?
                    </Link>
                  </Grid2>
                </Grid2>
                <Grid2 item xs={12} style={{ marginTop: "16px" }}>
                  <Button
                    type="primary"
                    className="login-pageButton"
                    fullWidth
                    onClick={onSubmitVal}
                    disabled={loading}
                  >
                    Log in
                  </Button>
                </Grid2>

                <Grid2
                  item
                  xs={12}
                  display="flex"
                  justifyContent="center"
                  style={{ marginTop: "16px" }}
                >
                  <span className="below-signin-text">
                    By continuing, you agree to the &nbsp;
                    <a href="https://thingspace.verizon.com/legal.html">
                      Terms & Conditions
                    </a>
                    &nbsp; and
                    <br />
                    <a href="https://www.verizon.com/about/privacy/">
                      Privacy Policy
                    </a>
                    .
                  </span>
                </Grid2>
              </div>
            </div>
          </Grid2>
        </Grid2>
      </div>
    </>
  );
};

export default SignIn;
